import { ReactNode } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { SessionFormat } from '@app/api';
import { FormatIcon, NotificationPin, Tooltip } from 'components';
import mapValues from 'lodash/mapValues';

const useColorStyles = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    color: color.accessible,
    '& i.main-icon': {
      color: color.accessible,
    },
    '& div.icon-container': {
      backgroundColor: color.muted,
    },
  })),
);

const useStyles = createUseStyles((theme: Theme) => ({
  content: {
    padding: theme.marginBase * 2,
    minWidth: 230,
    borderRadius: theme.borderRadius.std,
    border: 'solid 1px',
    borderColor: theme.colors.grayBorder,
    backgroundColor: theme.colors.white,
  },
  contentClickable: {
    cursor: 'pointer',
    transition: 'box-shadow .2s',
    '&:hover': {
      boxShadow: theme.boxShadow.base,
    },
  },
  title: {
    ...theme.fonts.label,
    paddingLeft: theme.marginBase,
    textTransform: 'uppercase',
  },
  firstLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: theme.height.tiny,
  },
  firstLineMargin: {
    marginBottom: theme.marginBase * 2,
  },
  spacer: {
    flexGrow: 1,
  },
  iconContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: theme.height.tiny,
    minWidth: theme.height.tiny,
    padding: 6,

    backgroundColor: theme.colors.white,
    borderRadius: theme.height.tiny / 2,
  },
  icon: {
    fontSize: theme.iconSize.regular,
  },
  secondLine: {
    ...theme.fonts.h4,
    color: 'inherit',

    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    transition: 'max-height 0.5s ease, opacity 0.5s ease, padding 0.5s ease',
  },
  collapsed: {
    maxHeight: 0,
    opacity: 0,
    padding: 0,
    pointerEvents: 'none',
  },
  helpContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase / theme.marginBase,
    marginLeft: theme.marginBase / 2,
    height: theme.iconSize.regular,
  },
  allowOverflow: {
    overflow: 'visible',
  },
}));

const usePinStyles = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    borderColor: color.accessible,
  })),
);

interface Props {
  title?: ReactNode;
  color?: Colors;
  icon?: string;
  pin?: boolean;
  formats?: SessionFormat[];
  className?: string;
  children?: ReactNode;
  onClick?: () => any;
  actionButton?: ReactNode;
  help?: ReactNode;
  isCollapsed?: boolean;
  allowOverflow?: boolean;
}

export const SessionBoxInfo = ({
  title,
  color = 'blue',
  icon,
  pin = false,
  formats,
  className,
  children,
  onClick,
  actionButton,
  help,
  isCollapsed = false,
  allowOverflow,
}: Props) => {
  const classes = useStyles();
  const colorClasses = useColorStyles();
  const pinClasses = usePinStyles();

  return (
    <div
      className={classnames(
        classes.content,
        colorClasses[color],
        {
          [classes.contentClickable]: !!onClick,
          [pinClasses[color]]: !!pin,
        },
        className,
      )}
      onClick={onClick}
    >
      {title && (
        <div
          className={classnames(classes.firstLine, {
            [classes.firstLineMargin]: !isCollapsed,
          })}
        >
          {icon && (
            <div
              className={classnames(classes.iconContainer, 'icon-container')}
            >
              {pin && <NotificationPin />}
              <i className={classnames(classes.icon, icon, 'main-icon')} />
            </div>
          )}
          {formats && (
            <div>
              <FormatIcon format={formats} />
            </div>
          )}
          <h4 className={classes.title}>{title}</h4>
          {help && (
            <div className={classes.helpContainer}>
              <Tooltip placement="top" arrow={true} title={help}>
                <i
                  className={classnames(
                    classes.icon,
                    'ri-question-line',
                    'main-icon',
                  )}
                ></i>
              </Tooltip>
            </div>
          )}
          <div className={classes.spacer} />
          {actionButton}
        </div>
      )}
      <div
        className={classnames(classes.secondLine, {
          [classes.collapsed]: isCollapsed,
          [classes.allowOverflow]: allowOverflow,
        })}
      >
        {children}
      </div>
    </div>
  );
};
